if (!customElements.get('product-bundle-selector')) {
  customElements.define(
    'product-bundle-selector',
    class ProductBundleSelector extends HTMLElement {
      connectedCallback() {
        this.scheduleInit();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
      }

      scheduleInit() {
        const tryInit = () => {
          if (this.initialized) return true;
          if (!document.getElementById(this.dataset.formId)) return false;
          this.init();
          return true;
        };

        if (tryInit()) return;

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => tryInit(), { once: true });
        } else {
          requestAnimationFrame(() => tryInit());
        }
      }

      init() {
        if (this.initialized) return;

        this.form = document.getElementById(this.dataset.formId);
        if (!this.form) return;

        this.initialized = true;
        this.sectionId = this.dataset.sectionId;
        this.discountPercent = parseFloat(this.dataset.bundleDiscountPercent) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.currentKitPrice = parseInt(this.dataset.initialKitPrice, 10) || 0;
        this.currentKitCompareAt = parseInt(this.dataset.initialKitCompareAt, 10) || 0;
        this.addons = this.readAddonsFromDom();

        this.kitPriceEl = this.querySelector('[data-kit-price]');
        this.bundlePriceEl = this.querySelector('[data-bundle-price]');
        this.propertyInput = this.querySelector('[data-bundle-property]');
        this.kitLabel = this.querySelector('input[value="kit"]')
          ?.closest('label')
          ?.querySelector('.product-bundle__option-title')
          ?.textContent?.trim();
        this.bundleLabel = this.querySelector('input[value="bundle"]')
          ?.closest('.product-bundle__option')
          ?.querySelector('.product-bundle__option-title')
          ?.textContent?.trim();
        this.selectedAddonId = null;
        this.featuredId = 'kit';
        this.bogoFirstSelected = false;
        this.galleryIndex = 0;
        this.packSize = 1;
        this.qty = 1;
        this.boxOpen = false;
        this.buildOpen = false;
        this.panel = this.querySelector('[data-bundle-panel]');
        this.chooserGrid = this.querySelector('[data-bundle-chooser-grid]');
        this.kitData = this.readKitFromDom();
        this.bogo = this.readBogoConfig();
        if (this.bogo) this.packSize = 2;

        this.querySelectorAll('[data-bundle-input]').forEach((input) => {
          input.addEventListener('change', (event) => this.onSelectionChange(event));
        });

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data?.sectionId !== this.sectionId) return;
            if (this.isPicker() && String(this.featuredId) !== 'kit') return;
            if (event.data?.variant?.price != null) {
              this.currentKitPrice = event.data.variant.price;
              this.currentKitCompareAt = event.data.variant.compare_at_price || 0;
              if (this.kitData) {
                this.kitData.price = this.currentKitPrice;
                this.kitData.compareAt = this.currentKitCompareAt;
              }
              this.updatePrices();
              if (this.isPicker()) this.renderPickerShop();
            }
          });
        }

        this.renderChooser();
        this.renderIncludedProducts();
        this.syncBundlePanel();
        this.updatePrices();
        this.syncQuantityToForm();
      }

      isPicker() {
        return this.dataset.layout === 'picker';
      }

      readKitFromDom() {
        const kitScript = this.querySelector('[data-bundle-kit]');
        if (!kitScript) return null;

        try {
          return JSON.parse(kitScript.textContent);
        } catch (error) {
          console.error('Product bundle selector: invalid kit data', error);
          return null;
        }
      }

      readBogoConfig() {
        if (!this.hasAttribute('data-bogo') || !this.isPicker()) return null;
        const script = this.querySelector('[data-bogo-config]');
        try {
          const config = JSON.parse(script?.textContent || '{}');
          return { ...config, price: parseInt(config.price, 10) || 13400 };
        } catch (error) {
          console.error('Product bundle selector: invalid offer config', error);
          return null;
        }
      }

      readAddonsFromDom() {
        const addonsScript = this.querySelector('[data-bundle-addons]');
        if (!addonsScript) return [];

        try {
          const addons = JSON.parse(addonsScript.textContent);
          const seen = new Set();
          return Array.isArray(addons)
            ? addons.filter((addon) => {
                if (!addon?.id || seen.has(String(addon.id))) return false;
                seen.add(String(addon.id));
                return true;
              })
            : [];
        } catch (error) {
          console.error('Product bundle selector: invalid addon data', error);
          return [];
        }
      }

      getAvailableAddons() {
        return this.addons.filter((addon) => addon.available !== false);
      }

      getSelectedAddon() {
        if (this.isPicker() && this.packSize !== 2) return null;
        if (this.isPicker() && this.packSize === 2) {
          if (!this.selectedAddonId) return null;
          const item = this.getPickerCatalog().find((kit) => String(kit.catalogId) === String(this.selectedAddonId));
          if (!item) return null;
          return {
            id: item.variantId || item.id,
            title: item.title,
            price: item.price || 0,
            compareAt: item.compareAt || 0,
            image: item.image || (item.images && item.images[0]) || null,
            imageAlt: item.imageAlt || item.title || '',
            available: item.available !== false,
          };
        }
        if (!this.selectedAddonId) return null;
        if (this.isPicker() && String(this.selectedAddonId) === 'kit') {
          const kit = this.kitData || {};
          return {
            id: kit.variantId,
            title: kit.title || this.dataset.productTitle || 'Kit',
            price: kit.price || parseInt(this.dataset.initialKitPrice, 10) || 0,
            compareAt: kit.compareAt || parseInt(this.dataset.initialKitCompareAt, 10) || 0,
            image: kit.image || this.dataset.kitImage || null,
            imageAlt: kit.imageAlt || this.dataset.productTitle || '',
            available: kit.available !== false,
          };
        }
        return this.getAvailableAddons().find((addon) => String(addon.id) === String(this.selectedAddonId)) || null;
      }

      getSelectedAddons() {
        const selected = this.getSelectedAddon();
        return selected ? [selected] : [];
      }

      isBundleSelected() {
        return this.querySelector('[data-bundle-input][value="bundle"]:checked') !== null;
      }

      syncBundlePanel() {
        if (this.isPicker()) {
          this.updatePickerSlots();
          return;
        }
        if (this.panel) this.panel.hidden = !this.isBundleSelected();
      }

      getKitProduct() {
        const kit = this.kitData || {};
        return {
          id: 'kit',
          title: kit.title || this.dataset.productTitle || this.kitLabel || 'Kit only',
          price: this.currentKitPrice,
          compareAt: this.currentKitCompareAt,
          image: kit.image || this.dataset.kitImage || null,
          imageAlt: kit.imageAlt || this.dataset.kitImageAlt || this.dataset.productTitle || '',
          images: Array.isArray(kit.images) && kit.images.length ? kit.images : [kit.image || this.dataset.kitImage].filter(Boolean),
          reviewCount: kit.reviewCount || 0,
          reviewRating: kit.reviewRating || 0,
          url: kit.url || this.dataset.kitUrl || null,
          isCurrent: true,
        };
      }

      /** Compare-at when higher than price; otherwise the selling price. */
      effectiveCompareAt(price, compareAt) {
        const compare = parseInt(compareAt, 10) || 0;
        return compare > price ? compare : price;
      }

      /**
       * Per-kit bundle deal, rounded to the dollar. Cart Function uses the same rule
       * and sums these — it does not re-round the combined compare-at total.
       */
      getBundleDealPrice(price, compareAt) {
        return this.getDiscountedPrice(this.effectiveCompareAt(price, compareAt));
      }

      getIncludedProducts() {
        const selected = this.getSelectedAddon();
        return selected ? [this.getKitProduct(), selected] : [this.getKitProduct()];
      }

      renderChooser() {
        if (this.isPicker()) {
          this.renderPickerShop();
          return;
        }

        if (!this.chooserGrid) return;

        const addons = this.getAvailableAddons();
        if (addons.length === 0) {
          this.chooserGrid.innerHTML = '';
          return;
        }

        this.chooserGrid.innerHTML = addons
          .map((addon) => {
            const selected = String(addon.id) === String(this.selectedAddonId);
            const image = addon.image
              ? `<img class="product-bundle__chooser-image" src="${this.escapeAttribute(addon.image)}" alt="${this.escapeAttribute(addon.imageAlt || addon.title)}" width="72" height="72" loading="lazy">`
              : '';

            return `<button
              type="button"
              class="product-bundle__chooser-item${selected ? ' is-selected' : ''}"
              data-bundle-choose="${this.escapeAttribute(String(addon.id))}"
              aria-pressed="${selected ? 'true' : 'false'}"
            >
              ${image}
              <span class="product-bundle__chooser-title">${this.escapeHtml(addon.title)}</span>
              <span class="product-bundle__chooser-price">${this.renderBundleDealPriceMarkup(addon.price, addon.compareAt || 0)}</span>
            </button>`;
          })
          .join('');

        this.bindChooserButtons();
      }

      bindChooserButtons() {
        this.chooserGrid.querySelectorAll('[data-bundle-choose]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.selectAddon(button.getAttribute('data-bundle-choose'));
          });
        });
      }

      getPickerCatalog() {
        const kit = this.kitData || {};
        const pageKit = {
          ...kit,
          id: 'kit',
          catalogId: 'kit',
          variantId: kit.variantId,
          title: kit.title || this.dataset.productTitle || 'Kit',
          price: kit.price || parseInt(this.dataset.initialKitPrice, 10) || 0,
          compareAt: kit.compareAt || parseInt(this.dataset.initialKitCompareAt, 10) || 0,
          image: kit.image || this.dataset.kitImage || null,
          imageAlt: kit.imageAlt || this.dataset.kitImageAlt || this.dataset.productTitle || '',
          images:
            Array.isArray(kit.images) && kit.images.length
              ? kit.images
              : [kit.image || this.dataset.kitImage].filter(Boolean),
          pieces: kit.pieces,
          size: kit.size,
          difficulty: kit.difficulty,
          buildTime: kit.buildTime,
          boxItems: Array.isArray(kit.boxItems) ? kit.boxItems : [],
          buildVideo: kit.buildVideo || null,
          buildPoster: kit.buildPoster || null,
          description: kit.description,
        };
        const addons = this.getAvailableAddons().map((addon) => ({
          ...addon,
          catalogId: String(addon.id),
          variantId: addon.id,
        }));
        return [pageKit, ...addons];
      }

      getFeaturedKit() {
        const catalog = this.getPickerCatalog();
        return catalog.find((item) => String(item.catalogId) === String(this.featuredId)) || catalog[0];
      }

      getAltKits() {
        return this.getPickerCatalog().filter((item) => String(item.catalogId) !== String(this.featuredId));
      }

      formatSize(size) {
        const raw = String(size || '23 cm').replace(/\s*tall/i, '').trim();
        const parts = raw.split(/\s*x\s*/i);
        if (parts.length >= 2) {
          const last = parts[parts.length - 1].replace(/[^\d.]/g, '');
          if (last) return `${last.replace(/\.0$/, '')} cm`;
        }
        return raw;
      }

      formatBuildTime(value) {
        const raw = String(value || '6–8 hours').trim();
        const withoutHours = raw.replace(/\s*hours?\s*$/i, '').replace(/\s+to\s+/gi, '–').trim();
        return withoutHours ? `${withoutHours} hrs` : raw;
      }

      percentOff(price, compareAt) {
        if (compareAt > price) return Math.round((1 - price / compareAt) * 100);
        return 0;
      }

      pickerBoxItems(kit) {
        if (Array.isArray(kit?.boxItems) && kit.boxItems.length) return kit.boxItems;
        return [
          { title: 'Pre-numbered wooden pieces', text: 'Every piece is marked, so you always know what comes next.' },
          { title: 'Decorative inserts', text: 'The tiny details that make the scene feel lived in.' },
          { title: 'Illustrated step-by-step instructions', text: 'Clear pictures walk you through the build.' },
          { title: 'Pre-wired LED lighting', text: 'No wiring. You place it, then switch it on.' },
          { title: '[FREE] Acrylic dust cover', text: 'Keeps the finished piece clean on the shelf.' },
          { title: '[FREE] Build tools', text: 'The tools you need are already in the box.' },
          { title: 'Gift-ready packaging', text: 'Ready to give, without another bag or box.' },
        ];
      }

      pickerFreeBoxItems(kit) {
        return this.pickerBoxItems(kit).filter((item) => /free/i.test(item.title || ''));
      }

      pickerFreePerkLabel(freeItems) {
        const names = (freeItems || [])
          .map((item) => String(item.title || '').replace(/^\s*\[?\s*free\s*\]?\s*/i, '').trim())
          .filter(Boolean);
        if (!names.length) return 'FREE extras included';
        if (names.length === 1) return `FREE ${names[0]}`;
        if (names.length === 2) return `FREE ${names[0]} + ${names[1]}`;
        return `FREE ${names.slice(0, -1).join(', ')} + ${names[names.length - 1]}`;
      }

      renderPickerShop() {
        const featured = this.getFeaturedKit();
        const alts = this.getAltKits();
        const altsEl = this.altsEl || (this.altsEl = this.querySelector('.pb-alts'));
        if (altsEl?.closest('[data-picker-featured]')) this.querySelector('.pb-picker')?.appendChild(altsEl);
        this.renderFeaturedCard(featured);
        this.renderAltKits(alts);
        if (altsEl && this.hasAttribute('data-desktop-split')) {
          this.querySelector('.pb-feature__details > .pb-feature__checkout')?.before(altsEl);
        }
        if (altsEl) altsEl.hidden = Boolean(this.bogo);
        this.bindPickerShop();
        this.bindLiveGallery();
        this.bindBoxCardsScroll();
        this.placeAfterpay(this.bogo ? this.bogo.price : featured?.price);
        this.placeRating();
        this.placeEditorial();
        this.placeSpecs(featured);
        this.syncProductSpecsTab(featured);
        this.placeSnippets();
        this.placeMotives();
        this.placeAssurance();
        this.placePayments();
      }

      specsTabSize(size) {
        const raw = String(size || '').trim();
        if (!raw) return '';
        return /tall/i.test(raw) ? raw : `${raw} tall`;
      }

      productSpecsTabHtml(kit) {
        if (!kit) return '';
        const piecesNum = String(kit.pieces || '').match(/\d[\d,]*/)?.[0];
        const pieces = piecesNum ? `${piecesNum} pre-numbered wooden pieces` : '';
        const buildTime = kit.buildTime ? String(kit.buildTime).trim() : '';
        const size = this.specsTabSize(kit.size);
        const freeNames = this.pickerFreeBoxItems(kit)
          .map((item) => String(item.title || '').replace(/^\s*\[?\s*free\s*\]?\s*/i, '').trim().toLowerCase())
          .filter(Boolean);
        const includedParts = ['Illustrated instructions', ...freeNames];
        const included = includedParts.filter(Boolean).join(', ') || 'Illustrated instructions, build tools';
        const rows = [
          pieces && ['Pieces', pieces],
          buildTime && ['Build time', buildTime],
          size && ['Finished size', size],
          ['Lighting', 'Pre-wired warm LEDs (battery powered)'],
          ['Materials', 'Laser-cut wood, decorative inserts'],
          ['Included', included],
          ['Not included', 'Glue and batteries'],
          ['Recommended age', '14+'],
        ].filter(Boolean);

        return `<ul>${rows
          .map(([label, value]) => `<li><strong>${this.escapeHtml(label)}:</strong> ${this.escapeHtml(value)}</li>`)
          .join('')}</ul>`;
      }

      syncProductSpecsTab(kit) {
        const targets = document.querySelectorAll('[data-ypt-kit-specs]');
        if (!targets.length || !kit) return;
        const html = this.productSpecsTabHtml(kit);
        if (!html) return;
        targets.forEach((node) => {
          node.innerHTML = html;
        });
      }

      placePayments() {
        if (!this.hasAttribute('data-bogo')) return;
        const anchor = this.querySelector('[data-picker-featured] [data-bogo-single-root]');
        const source = (this.closest('.product__info-container') || document).querySelector('.product-form__payments .list-payment');
        if (!anchor || !source) return;
        const wrap = document.createElement('div');
        wrap.className = 'pb-payments';
        wrap.appendChild(source.cloneNode(true));
        anchor.after(wrap);
        wrap.insertAdjacentHTML(
          'afterend',
          `<ul class="pb-trust" role="list">
            <li class="pb-trust__item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>
              Secure Checkout
            </li>
            <li class="pb-trust__item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.4"/></svg>
              Dispatches &amp; Ships from Sydney
            </li>
          </ul>`
        );
      }

      placeMotives() {
        if (!this.hasAttribute('data-desktop-split')) return;
        const anchor =
          this.querySelector('[data-picker-featured] [data-picker-snippets]') ||
          this.querySelector('[data-picker-featured] .pb-specs');
        if (!anchor) return;
        const icons = {
          gift: '<rect x="3.5" y="8.5" width="17" height="4" rx="1"/><path d="M5 12.5V20h14v-7.5"/><path d="M12 8.5V20"/><path d="M12 8.5C10.6 5 6.5 4.6 6.5 7c0 1.3 2.2 1.5 5.5 1.5zM12 8.5C13.4 5 17.5 4.6 17.5 7c0 1.3-2.2 1.5-5.5 1.5z"/>',
          hobby: '<path d="M12 3.5a8.5 8.5 0 1 0 0 17c1.2 0 1.8-.9 1.4-1.9-.5-1.2.3-2.3 1.6-2.3h1.8a3.7 3.7 0 0 0 3.7-3.7C20.5 7.3 16.7 3.5 12 3.5z"/><circle cx="7.8" cy="11" r="1.1"/><circle cx="10.6" cy="7.6" r="1.1"/><circle cx="14.9" cy="7.8" r="1.1"/>',
          shelf: '<path d="M3.5 19.5h17"/><path d="M5.5 19.5V11l4-3.5 4 3.5v8.5"/><path d="M8.5 19.5v-4h2v4"/><path d="M17.5 19.5v-6"/><path d="M15.8 13.5h3.4l-.9-3.2h-1.6z"/>',
        };
        const items = [
          { icon: 'gift', title: 'Gift', text: "A gift they'll build &amp; treasure" },
          { icon: 'hobby', title: 'Hobby', text: 'A calm, screen-free escape' },
          { icon: 'shelf', title: 'Shelf Decor', text: 'A warm glow for any shelf' },
        ];
        anchor.insertAdjacentHTML(
          'afterend',
          `<div class="pb-motives">
            <p class="pb-motives__eyebrow">What will yours be for?</p>
            <ul class="pb-motives__list" role="list">
              ${items
                .map(
                  (item) => `<li class="pb-motives__item">
                    <span class="pb-motives__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${icons[item.icon]}</svg></span>
                    <strong class="pb-motives__title">${item.title}</strong>
                    <span class="pb-motives__text">${item.text}</span>
                  </li>`
                )
                .join('')}
            </ul>
          </div>`
        );
      }

      placeAssurance() {
        if (!this.hasAttribute('data-desktop-split')) return;
        const anchor = this.querySelector('[data-picker-featured] .pb-specs');
        if (!anchor) return;
        const items = ['Illustrated<br>instructions', 'Beginner-<br>friendly', 'Completion<br>guarantee'];
        anchor.insertAdjacentHTML(
          'afterend',
          `<ul class="pb-assure" role="list">
            ${items
              .map(
                (text) => `<li class="pb-assure__item">
                  <span class="pb-assure__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m6.5 12.5 3.6 3.5 7.4-8"/></svg></span>
                  <span class="pb-assure__text">${text}</span>
                </li>`
              )
              .join('')}
          </ul>`
        );
      }

      snippetsSource() {
        return (
          this.closest('.product__info-container')?.querySelector('[data-picker-snippets-source]') ||
          document.querySelector('[data-picker-snippets-source]')
        );
      }

      parkSnippets() {
        const source = this.snippetsSource();
        const widget = this.querySelector('[data-picker-snippets] > *') || this.snippetsWidget;
        if (!source || !widget) return;
        this.snippetsWidget = widget;
        source.appendChild(widget);
      }

      placeSnippets() {
        const source = this.snippetsSource();
        const widget = this.snippetsWidget || source?.firstElementChild;
        const anchor = this.querySelector('[data-picker-featured] .pb-specs');
        const kit = this.getFeaturedKit();
        const loox = widget?.querySelector('loox-snippets-widget');
        if (!widget || !anchor || !kit) return;
        if (loox && kit.productId) {
          if (!(kit.reviewCount > 0)) return;
          if (loox.getAttribute('product-id') !== String(kit.productId)) {
            loox.setAttribute('product-id', String(kit.productId));
            loox.setAttribute('review-count', String(kit.reviewCount));
          }
        } else if (String(this.featuredId) !== 'kit') {
          return;
        }
        this.snippetsWidget = widget;
        const slot = document.createElement('div');
        slot.className = 'pb-feature__snippets';
        slot.setAttribute('data-picker-snippets', '');
        slot.appendChild(widget);
        anchor.after(slot);
      }

      kitHeight(size) {
        const numbers = String(size || '').match(/\d+(?:\.\d+)?/g);
        if (!numbers) return '';
        const value = parseFloat(numbers[numbers.length - 1]);
        return `${Number.isInteger(value) ? value : value.toFixed(1)} cm`;
      }

      placeSpecs(kit) {
        if (!kit || !this.hasAttribute('data-desktop-split')) return;
        const anchor =
          this.querySelector('[data-picker-featured] [data-picker-editorial]') ||
          this.querySelector('[data-picker-featured] .pb-feature__top');
        if (!anchor) return;
        const icons = {
          pieces: '<path d="M10 5V4a2 2 0 1 1 4 0v1h4a1 1 0 0 1 1 1v4h-1a2 2 0 1 0 0 4h1v4a1 1 0 0 1-1 1h-4v-1a2 2 0 1 0-4 0v1H6a1 1 0 0 1-1-1v-4h1a2 2 0 1 0 0-4H5V6a1 1 0 0 1 1-1h4z"/>',
          time: '<circle cx="12" cy="12" r="8.5"/><path d="M12 7.5V12l3 2.5"/>',
          led: '<path d="M9.5 17.5h5M10 20h4M12 6.5a4.5 4.5 0 0 0-2.6 8.2c.4.3.6.7.6 1.2v1.6h4v-1.6c0-.5.2-.9.6-1.2A4.5 4.5 0 0 0 12 6.5z"/><path d="M12 2.5v1.6M4.5 5.5l1.1 1.1M19.5 5.5l-1.1 1.1M2.5 11.5h1.6M20 11.5h1.6"/>',
          height: '<rect x="5.5" y="3.5" width="4.5" height="17" rx="1.2"/><rect x="14" y="3.5" width="4.5" height="17" rx="1.2"/><path d="M7.75 7v10M16.25 7v10"/>',
        };
        const pieces = String(kit.pieces || '').match(/\d[\d,]*/)?.[0];
        const items = [
          pieces && { icon: 'pieces', stat: pieces, label: 'Pieces' },
          kit.buildTime && { icon: 'time', stat: this.formatBuildTime(kit.buildTime), label: 'Build time' },
          { icon: 'led', stat: 'Warm LEDs', label: 'Included' },
          this.kitHeight(kit.size) && { icon: 'height', stat: this.kitHeight(kit.size), label: 'Tall' },
        ].filter(Boolean);

        anchor.insertAdjacentHTML(
          'afterend',
          `<ul class="pb-specs" role="list">
            ${items
              .map(
                (item) => `<li class="pb-specs__item">
                  <svg class="pb-specs__icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${icons[item.icon]}</svg>
                  <span class="pb-specs__stat">${this.escapeHtml(item.stat)}</span>
                  <span class="pb-specs__label">${this.escapeHtml(item.label)}</span>
                </li>`
              )
              .join('')}
          </ul>`
        );
      }

      editorialSource() {
        return (
          this.closest('.product__info-container')?.querySelector('[data-picker-editorial-source]') ||
          document.querySelector('[data-picker-editorial-source]')
        );
      }

      parkEditorial() {
        const source = this.editorialSource();
        const widget = this.editorialWidget;
        if (!source || !widget || widget.parentElement === source) return;
        source.appendChild(widget);
      }

      placeEditorial() {
        const source = this.editorialSource();
        const top = this.querySelector('[data-picker-featured] .pb-feature__top');
        if (!source || !top) return;
        const slot = document.createElement('div');
        slot.className = 'pb-feature__editorial';
        slot.setAttribute('data-picker-editorial', '');

        if (String(this.featuredId) === 'kit') {
          const widget = this.editorialWidget || source.querySelector('.ypp-editorial');
          if (!widget) return;
          this.editorialWidget = widget;
          slot.appendChild(widget);
        } else {
          const html = this.getFeaturedKit()?.editorialHtml;
          if (!html) return;
          slot.innerHTML = `<div class="ypp-editorial rte">
            <span class="ypp-editorial__rule" aria-hidden="true">&nbsp;</span>
            <div class="ypp-editorial__body">${html}</div>
            <span class="ypp-editorial__rule" aria-hidden="true">&nbsp;</span>
          </div>`;
        }
        top.after(slot);
      }

      afterpaySource() {
        return (
          this.closest('.product__info-container')?.querySelector('[data-picker-afterpay-source]') ||
          document.querySelector('[data-picker-afterpay-source]')
        );
      }

      ratingSource() {
        return (
          this.closest('.product__info-container')?.querySelector('[data-picker-rating-source]') ||
          document.querySelector('[data-picker-rating-source]')
        );
      }

      parkRating() {
        const source = this.ratingSource();
        const hosted = this.querySelector('[data-picker-rating] .ypp-loox-rating, [data-picker-rating] .loox-rating');
        const widget = hosted || this.ratingWidget;
        if (!source || !widget) return;
        this.ratingWidget = widget;
        source.appendChild(widget);
      }

      placeRating() {
        const slot = this.querySelector('[data-picker-rating]');
        const source = this.ratingSource();
        const widget =
          this.ratingWidget ||
          source?.querySelector('.ypp-loox-rating, .loox-rating, .shopify-app-block') ||
          source?.firstElementChild;
        if (!slot) return;
        if (!widget) {
          if ((this.ratingRetries || 0) < 8) {
            this.ratingRetries = (this.ratingRetries || 0) + 1;
            setTimeout(() => this.placeRating(), 250);
          }
          return;
        }
        this.ratingRetries = 8;
        this.ratingWidget = widget;
        if (widget.parentElement !== slot) slot.appendChild(widget);
      }

      parkAfterpay() {
        const source = this.afterpaySource();
        const hosted = this.querySelector('[data-picker-afterpay] .shopify-block, [data-picker-afterpay] square-placement');
        const widget = hosted || this.afterpayWidget;
        if (!source || !widget) return;
        this.afterpayWidget = widget;
        source.appendChild(widget);
      }

      placeAfterpay(cents) {
        const slot = this.querySelector('[data-picker-afterpay]');
        const source = this.afterpaySource();
        const widget =
          this.afterpayWidget ||
          source?.querySelector('.shopify-block, square-placement, [id*="afterpay"]') ||
          source?.firstElementChild;
        if (!slot) return;
        if (!widget) {
          if ((this.afterpayRetries || 0) < 8) {
            this.afterpayRetries = (this.afterpayRetries || 0) + 1;
            setTimeout(() => this.placeAfterpay(this.bogo ? this.bogo.price : this.getFeaturedKit()?.price), 250);
          }
          return;
        }
        this.afterpayRetries = 8;
        this.afterpayWidget = widget;
        if (widget.parentElement !== slot) slot.appendChild(widget);
        this.updateAfterpayAmount(cents);
      }

      updateAfterpayAmount(cents) {
        const amount = Number(cents);
        if (!Number.isFinite(amount) || amount <= 0) return;
        const dollars = Math.round(amount) / 100;
        const value = Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
        const placement =
          this.querySelector('square-placement') ||
          this.afterpayWidget?.querySelector?.('square-placement') ||
          this.afterpayWidget;
        if (!placement || !placement.setAttribute) return;
        placement.setAttribute('data-amount', value);
      }

      bindBoxCardsScroll() {
        const track = this.querySelector('[data-picker-box-cards]');
        const scrollUi = this.querySelector('[data-picker-box-scroll]');
        const thumb = this.querySelector('[data-picker-box-thumb]');
        if (!track) return;

        const update = () => {
          const max = track.scrollWidth - track.clientWidth;
          const canScroll = max > 1;
          if (scrollUi) scrollUi.hidden = !canScroll;
          if (!canScroll || !thumb) return;
          const visible = track.clientWidth / track.scrollWidth;
          const progress = track.scrollLeft / max;
          const thumbWidth = Math.min(100, Math.max(18, visible * 100));
          const travel = 100 - thumbWidth;
          thumb.style.width = `${thumbWidth}%`;
          thumb.style.transform = `translateX(${progress * (travel / thumbWidth) * 100}%)`;
        };

        track.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        requestAnimationFrame(update);
      }

      renderFeaturedCard(kit) {
        const root = this.querySelector('[data-picker-featured]');
        if (!root || !kit) return;
        this.parkAfterpay();
        this.parkRating();
        this.parkEditorial();
        this.parkSnippets();

        const images = (kit.images && kit.images.length ? kit.images : [kit.image]).filter(Boolean);
        const galleryItems = this.getGalleryItems(kit, images);
        if (this.galleryIndex >= galleryItems.length) this.galleryIndex = 0;
        const current = images[this.galleryIndex] || images[0] || '';
        const off = this.percentOff(kit.price, kit.compareAt || 0);
        const boxItems = this.pickerBoxItems(kit);
        const freeBoxItems = this.pickerFreeBoxItems(kit);
        const perkItems = freeBoxItems.slice(0, 2);
        const perkLabel = this.pickerFreePerkLabel(perkItems);
        const boxSubLabel = this.pickerFreePerkLabel(freeBoxItems);
        const atcPrice = this.getFeaturedAtcPrice(kit) * (this.qty || 1);
        const buildVideo = kit.buildVideo || '';
        const buildPoster = kit.buildPoster || '';
        const { sale: pairSale, compare: pairCompare } = this.getPairPrice(kit);
        const packTwo = this.packSize === 2;
        const titleBelow = this.hasAttribute('data-title-below-gallery');
        const split = this.hasAttribute('data-desktop-split');
        const top = `<div class="pb-feature__top${titleBelow ? ' pb-feature__top--below' : ''}">
              ${this.hasAttribute('data-bogo') ? `<p class="pb-feature__eyebrow">DIY 3D Miniature${kit.kitType ? ` · ${this.escapeHtml(kit.kitType)}` : ''}</p>` : ''}
              <h3 class="pb-feature__title">${this.escapeHtml(kit.title)}</h3>
              <div class="pb-feature__rating" data-picker-rating></div>
            </div>`;

        root.innerHTML = `
          <article class="pb-feature__card${split ? ' pb-feature__card--split' : ''}">
            ${titleBelow ? '' : top}
            ${split ? '<div class="pb-feature__media">' : ''}
            ${this.hasAttribute('data-gallery-live') ? this.renderLiveGallery(kit, galleryItems) : `<div class="pb-feature__gallery">
              <div class="pb-feature__stage">
                ${current ? `<img class="pb-feature__image" data-picker-hero src="${this.escapeAttribute(current)}" alt="${this.escapeAttribute(kit.imageAlt || kit.title)}" width="900" height="900">` : ''}
                ${images.length > 1 ? `
                  <button type="button" class="pb-feature__nav pb-feature__nav--prev" data-picker-prev aria-label="Previous photo">${this.chevronIcon('prev')}</button>
                  <button type="button" class="pb-feature__nav pb-feature__nav--next" data-picker-next aria-label="Next photo">${this.chevronIcon('next')}</button>
                  <span class="pb-feature__count">${this.galleryIndex + 1} / ${images.length}</span>
                ` : ''}
                ${current ? `<button type="button" class="pb-feature__expand" data-picker-expand aria-label="View larger photo">${this.expandIcon()}</button>` : ''}
              </div>
              ${images.length > 1 ? `<div class="pb-feature__thumbs" role="group" aria-label="More photos">${this.renderFeaturedThumbs(images)}</div>` : ''}
            </div>`}
            ${split ? '</div><div class="pb-feature__details">' : ''}
            ${titleBelow ? top : ''}
            <div class="pb-feature__buy">
              <p class="pb-feature__price">
                <span class="pb-feature__sale">${this.formatMoney(kit.price, false)}</span>
                ${kit.compareAt > kit.price ? `<s class="pb-feature__compare">${this.formatMoney(kit.compareAt, false)}</s>` : ''}
                ${off > 0 ? `<span class="pb-feature__off">${off}% OFF</span>` : ''}
              </p>
              <div class="pb-feature__afterpay" data-picker-afterpay></div>
            </div>
            <div class="pb-ship">
              <span class="pb-ship__icon" aria-hidden="true">${this.trustIcon('truck')}</span>
              <span class="pb-ship__copy">
                <strong class="pb-ship__title">This order ships free</strong>
                <span class="pb-ship__note">Australia-wide. No minimum spend.</span>
              </span>
            </div>
            <div class="pb-perk">
              <span class="pb-perk__icon" aria-hidden="true">${this.giftIcon()}</span>
              <span class="pb-perk__copy">
                <strong>${this.escapeHtml(perkLabel).replace(/ \+ /g, ' +&nbsp;')}</strong>
                <span>No extra cost</span>
              </span>
              ${perkItems.length ? `<span class="pb-perk__media">${perkItems
                .map((item) => (item.image ? `<span class="pb-perk__shot"><img src="${this.escapeAttribute(item.image)}" alt="" width="96" height="96" loading="lazy"></span>` : ''))
                .join('')}</span>` : ''}
            </div>
            <div class="pb-packs" role="radiogroup" aria-label="Choose one kit or two">
              <button type="button" class="pb-pack${!packTwo ? ' is-selected' : ''}" data-picker-pack="1" aria-pressed="${packTwo ? 'false' : 'true'}">
                <span class="pb-pack__body">
                  <span class="pb-pack__radio" aria-hidden="true"></span>
                  <span class="pb-pack__copy">
                    <strong>1 Kit</strong>
                    ${off > 0 ? `<span class="pb-pack__save">Save ${off}%</span>` : ''}
                    <span class="pb-pack__price">
                      <span>${this.formatMoney(kit.price, false)}</span>
                      ${kit.compareAt > kit.price ? `<s>${this.formatMoney(kit.compareAt, false)}</s>` : ''}
                    </span>
                  </span>
                </span>
                <span class="pb-pack__badge">Single kit</span>
              </button>
              <button type="button" class="pb-pack pb-pack--deal${packTwo ? ' is-selected' : ''}" data-picker-pack="2" aria-pressed="${packTwo ? 'true' : 'false'}">
                <span class="pb-pack__body">
                  <span class="pb-pack__radio" aria-hidden="true"></span>
                  <span class="pb-pack__copy">
                    <strong>2 Kits</strong>
                    ${this.discountPercent > 0 ? `<span class="pb-pack__save">Save ${Math.round(this.discountPercent)}%</span>` : ''}
                    <span class="pb-pack__price">
                      <span>${this.formatMoney(pairSale, false)}</span>
                      ${pairCompare > pairSale ? `<s>${this.formatMoney(pairCompare, false)}</s>` : ''}
                    </span>
                  </span>
                </span>
                <span class="pb-pack__badge">${this.sparkIcon()} Most popular</span>
              </button>
            </div>
            ${packTwo ? this.renderSecondKitMenu(kit) : ''}
            <div class="pb-feature__checkout">
              <button type="button" class="pb-feature__atc" data-picker-atc>
                ${this.bagIcon()}<span>Add to bag — ${this.formatMoney(atcPrice, false)}</span>
              </button>
            </div>
            <ul class="pb-feature__trust">
              <li>${this.trustIcon('wrench')}<span>Free replacement parts</span></li>
              <li>${this.trustIcon('check')}<span>Completion guarantee</span></li>
              <li>${this.trustIcon('box')}<span>30-day returns</span></li>
            </ul>
            <div class="pb-feature__box">
              <button type="button" class="pb-feature__box-toggle" data-picker-box aria-expanded="${this.boxOpen ? 'true' : 'false'}">
                ${this.boxIcon()}
                <span class="pb-feature__box-copy">
                  <span class="pb-feature__box-label">What's in the box</span>
                  <span class="pb-feature__box-sub">${this.escapeHtml(boxSubLabel).replace(/^FREE\s+/i, '<strong>FREE</strong> ')}</span>
                </span>
                <span class="pb-feature__chevron" aria-hidden="true">${this.chevronIcon(this.boxOpen ? 'up' : 'down')}</span>
              </button>
              <div class="pb-feature__box-panel" ${this.boxOpen ? '' : 'hidden'}>
                <div class="pb-box-wrap">
                  <div class="pb-box-cards" data-picker-box-cards>
                    ${boxItems
                      .map(
                        (item) => `<article class="pb-box-card">
                        <div class="pb-box-card__media">
                          ${item.image ? `<img src="${this.escapeAttribute(item.image)}" alt="" width="240" height="240" loading="lazy">` : ''}
                        </div>
                        <h4 class="pb-box-card__title">${this.escapeHtml(item.title || '')}</h4>
                        ${item.text ? `<p class="pb-box-card__text">${this.escapeHtml(item.text)}</p>` : ''}
                      </article>`
                      )
                      .join('')}
                  </div>
                  <div class="pb-box-scroll" data-picker-box-scroll hidden>
                    <div class="pb-box-bar" aria-hidden="true">
                      <span class="pb-box-bar-thumb" data-picker-box-thumb></span>
                    </div>
                  </div>
                </div>
                ${freeBoxItems.length ? `<p class="pb-box-free">${this.escapeHtml(boxSubLabel).replace(/^FREE\s+/i, '<strong>FREE</strong> ')} included in every kit.</p>` : ''}
                <p class="pb-box-note">Glue and batteries are not included.</p>
              </div>
            </div>
            ${
              buildVideo
                ? `<div class="pb-feature__box pb-feature__box--build">
              <button type="button" class="pb-feature__box-toggle" data-picker-build aria-expanded="${this.buildOpen ? 'true' : 'false'}">
                ${this.playIcon()}
                ${split
                  ? '<span class="pb-feature__box-copy"><span class="pb-feature__box-label">See how it comes together</span><span class="pb-feature__box-sub">Watch the build video</span></span>'
                  : '<span>See how it comes together</span>'}
                <span class="pb-feature__chevron" aria-hidden="true">${this.chevronIcon(this.buildOpen ? 'up' : 'down')}</span>
              </button>
              <div class="pb-feature__box-panel" ${this.buildOpen ? '' : 'hidden'}>
                <div class="pb-build-embed">
                  <video
                    data-picker-build-video
                    src="${this.escapeAttribute(buildVideo)}"
                    ${buildPoster ? `poster="${this.escapeAttribute(buildPoster)}"` : ''}
                    controls
                    playsinline
                    preload="metadata"
                    loop
                  ></video>
                </div>
              </div>
            </div>`
                : ''
            }
            ${split ? '</div>' : ''}
          </article>
        `;
        if (this.bogo) this.mountBogo(root, kit);
        if (split) {
          const media = root.querySelector('.pb-feature__media');
          const build = root.querySelector('.pb-feature__details > .pb-feature__box--build');
          if (build) media?.appendChild(build);
          const trust = root.querySelector('.pb-feature__details > .pb-feature__trust');
          const after = ['.pb-feature__box', '.pb-ship', '.pb-perk']
            .map((sel) => root.querySelector(`.pb-feature__details > ${sel}`))
            .filter(Boolean);
          if (trust) trust.after(...after);
        }
      }

      getGalleryItems(kit, images) {
        if (this.hasAttribute('data-gallery-video') && Array.isArray(kit.media) && kit.media.length) {
          return kit.media.filter((item) => item && item.src);
        }
        return images.map((src) => ({ type: 'image', src }));
      }

      renderLiveGallery(kit, items) {
        if (!items.length) return '<div class="pb-feature__gallery"></div>';
        const alt = this.escapeAttribute(kit.imageAlt || kit.title);
        const multi = items.length > 1;
        const stripNav = multi && this.hasAttribute('data-desktop-split');
        const thumbs = multi ? `<div class="pb-feature__thumbs" role="group" aria-label="More photos" data-pb-thumbs>
            ${items
              .map((item, index) => {
                const video = item.type === 'video';
                return `<button type="button" class="pb-feature__thumb${video ? ' pb-feature__thumb--video' : ''}${index === this.galleryIndex ? ' is-active' : ''}" data-pb-thumb="${index}" aria-label="${video ? 'Play video' : `View photo ${index + 1}`}" aria-pressed="${index === this.galleryIndex ? 'true' : 'false'}">
                  <img src="${this.escapeAttribute(video ? item.poster || '' : item.src)}" alt="" width="88" height="88" loading="lazy">
                  ${video ? '<span class="pb-feature__thumb-play" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg></span>' : ''}
                </button>`;
              })
              .join('')}
          </div>` : '';
        return `<div class="pb-feature__gallery pb-gallery" data-pb-gallery>
          <div class="pb-feature__stage">
            <div class="pb-gallery__track" data-pb-track>
              ${items
                .map((item, index) =>
                  item.type === 'video'
                    ? `<div class="pb-gallery__slide">
                    <video class="pb-feature__image pb-gallery__video" src="${this.escapeAttribute(item.src)}"${item.poster ? ` poster="${this.escapeAttribute(item.poster)}"` : ''} muted loop playsinline preload="metadata" aria-label="${alt} video"></video>
                  </div>`
                    : `<div class="pb-gallery__slide">
                    <img class="pb-feature__image" src="${this.escapeAttribute(item.src)}" alt="${index === 0 ? alt : ''}" width="900" height="900" loading="${index === 0 ? 'eager' : 'lazy'}" draggable="false">
                  </div>`
                )
                .join('')}
            </div>
            ${multi && !stripNav ? `
              <button type="button" class="pb-feature__nav pb-feature__nav--prev" data-pb-prev aria-label="Previous photo">${this.chevronIcon('prev')}</button>
              <button type="button" class="pb-feature__nav pb-feature__nav--next" data-pb-next aria-label="Next photo">${this.chevronIcon('next')}</button>
            ` : ''}
            ${multi ? `<span class="pb-feature__count" data-pb-count>${this.galleryIndex + 1} / ${items.length}</span>` : ''}
            <button type="button" class="pb-feature__expand" data-pb-expand aria-label="View larger photo">${this.expandIcon()}</button>
          </div>
          ${stripNav ? `<div class="pb-gallery__strip">
            <button type="button" class="pb-gallery__strip-nav" data-pb-prev aria-label="Previous photo">${this.chevronIcon('prev')}</button>
            ${thumbs}
            <button type="button" class="pb-gallery__strip-nav" data-pb-next aria-label="Next photo">${this.chevronIcon('next')}</button>
          </div>` : thumbs}
        </div>`;
      }

      bindLiveGallery() {
        const gallery = this.querySelector('[data-pb-gallery]');
        const track = gallery?.querySelector('[data-pb-track]');
        if (!track) return;

        const slides = track.children.length;
        const count = gallery.querySelector('[data-pb-count]');
        const thumbsWrap = gallery.querySelector('[data-pb-thumbs]');
        const thumbs = [...gallery.querySelectorAll('[data-pb-thumb]')];
        const syncVideos = (index) => {
          [...track.children].forEach((slide, i) => {
            const video = slide.querySelector('video');
            if (!video) return;
            if (i === index) {
              video.play()?.catch?.(() => {});
            } else {
              video.pause();
            }
          });
        };

        const setActive = (index) => {
          this.galleryIndex = index;
          syncVideos(index);
          if (count) count.textContent = `${index + 1} / ${slides}`;
          thumbs.forEach((thumb, i) => {
            thumb.classList.toggle('is-active', i === index);
            thumb.setAttribute('aria-pressed', i === index ? 'true' : 'false');
          });
          const active = thumbs[index];
          if (active && thumbsWrap) {
            const left = active.offsetLeft - (thumbsWrap.clientWidth - active.offsetWidth) / 2;
            thumbsWrap.scrollTo({ left, behavior: 'smooth' });
          }
        };

        const goTo = (index, smooth = true) => {
          const next = (index + slides) % slides;
          track.scrollTo({ left: next * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
          setActive(next);
        };

        if (this.galleryIndex >= slides) this.galleryIndex = 0;
        track.scrollLeft = this.galleryIndex * track.clientWidth;
        requestAnimationFrame(() => {
          track.scrollLeft = this.galleryIndex * track.clientWidth;
        });
        syncVideos(this.galleryIndex);

        let settle;
        track.addEventListener(
          'scroll',
          () => {
            clearTimeout(settle);
            settle = setTimeout(() => {
              const index = Math.round(track.scrollLeft / (track.clientWidth || 1));
              if (index !== this.galleryIndex) setActive(index);
            }, 90);
          },
          { passive: true }
        );

        gallery.querySelector('[data-pb-prev]')?.addEventListener('click', (event) => {
          event.preventDefault();
          goTo(this.galleryIndex - 1);
        });
        gallery.querySelector('[data-pb-next]')?.addEventListener('click', (event) => {
          event.preventDefault();
          goTo(this.galleryIndex + 1);
        });
        thumbs.forEach((thumb) => {
          thumb.addEventListener('click', (event) => {
            event.preventDefault();
            goTo(parseInt(thumb.getAttribute('data-pb-thumb'), 10) || 0);
          });
        });
        gallery.querySelector('[data-pb-expand]')?.addEventListener('click', (event) => {
          event.preventDefault();
          const items = [...track.children].map((slide) => {
            const video = slide.querySelector('video');
            if (video) return { type: 'video', src: video.getAttribute('src'), poster: video.getAttribute('poster') || '' };
            return { type: 'image', src: slide.querySelector('img')?.getAttribute('src') || '' };
          });
          syncVideos(-1);
          this.openLightbox(items, this.galleryIndex, (index) => goTo(index, false), event.currentTarget);
        });

        this.galleryGoTo = goTo;
        if (!this.galleryResizeBound) {
          this.galleryResizeBound = true;
          window.addEventListener('resize', () => {
            const current = this.querySelector('[data-pb-track]');
            if (current) current.scrollLeft = this.galleryIndex * current.clientWidth;
          });
        }
      }

      openLightbox(items, startIndex, onClose, returnFocus) {
        if (!items.length) return;
        let box = this.lightbox;
        if (!box) {
          box = document.createElement('div');
          box.className = 'pb-lightbox';
          box.setAttribute('role', 'dialog');
          box.setAttribute('aria-modal', 'true');
          box.setAttribute('aria-label', 'Product photos');
          box.hidden = true;
          box.innerHTML = `
            <div class="pb-lightbox__track" data-pb-lb-track></div>
            <button type="button" class="pb-lightbox__close" data-pb-lb-close aria-label="Close photos">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
            <button type="button" class="pb-lightbox__nav pb-lightbox__nav--prev" data-pb-lb-prev aria-label="Previous photo">${this.chevronIcon('prev')}</button>
            <button type="button" class="pb-lightbox__nav pb-lightbox__nav--next" data-pb-lb-next aria-label="Next photo">${this.chevronIcon('next')}</button>
            <span class="pb-lightbox__count" data-pb-lb-count></span>`;
          document.body.appendChild(box);
          this.lightbox = box;

          const track = box.querySelector('[data-pb-lb-track]');
          const countEl = box.querySelector('[data-pb-lb-count]');
          const indexNow = () => Math.round(track.scrollLeft / (track.clientWidth || 1));
          const total = () => track.children.length;
          const go = (index) => {
            const next = (index + total()) % total();
            track.scrollTo({ left: next * track.clientWidth, behavior: 'smooth' });
          };
          let settle;
          track.addEventListener(
            'scroll',
            () => {
              clearTimeout(settle);
              settle = setTimeout(() => {
                countEl.textContent = `${indexNow() + 1} / ${total()}`;
              }, 90);
            },
            { passive: true }
          );
          box.querySelector('[data-pb-lb-prev]').addEventListener('click', () => go(indexNow() - 1));
          box.querySelector('[data-pb-lb-next]').addEventListener('click', () => go(indexNow() + 1));
          box.querySelector('[data-pb-lb-close]').addEventListener('click', () => this.closeLightbox());
          track.addEventListener('click', (event) => {
            if (event.target.tagName !== 'IMG' && event.target.tagName !== 'VIDEO') this.closeLightbox();
          });
          box.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') this.closeLightbox();
            if (event.key === 'ArrowLeft') go(indexNow() - 1);
            if (event.key === 'ArrowRight') go(indexNow() + 1);
          });
          window.addEventListener('popstate', () => {
            if (!box.hidden) this.closeLightbox({ fromHistory: true });
          });
          box.lbIndexNow = indexNow;
        }

        const track = box.querySelector('[data-pb-lb-track]');
        track.innerHTML = items
          .map((item) =>
            item.type === 'video'
              ? `<div class="pb-lightbox__slide"><video src="${this.escapeAttribute(item.src)}"${item.poster ? ` poster="${this.escapeAttribute(item.poster)}"` : ''} controls muted loop playsinline preload="metadata"></video></div>`
              : `<div class="pb-lightbox__slide"><img src="${this.escapeAttribute(item.src)}" alt="" draggable="false"></div>`
          )
          .join('');
        const multi = items.length > 1;
        box.querySelectorAll('[data-pb-lb-prev], [data-pb-lb-next], [data-pb-lb-count]').forEach((el) => {
          el.hidden = !multi;
        });
        box.querySelector('[data-pb-lb-count]').textContent = `${startIndex + 1} / ${items.length}`;

        this.lightboxOnClose = onClose;
        this.lightboxReturnFocus = returnFocus;
        box.hidden = false;
        document.documentElement.classList.add('pb-lightbox-open');
        track.scrollLeft = startIndex * track.clientWidth;
        requestAnimationFrame(() => {
          track.scrollLeft = startIndex * track.clientWidth;
        });
        history.pushState({ pbLightbox: true }, '');
        box.querySelector('[data-pb-lb-close]').focus({ preventScroll: true });
      }

      closeLightbox({ fromHistory = false } = {}) {
        const box = this.lightbox;
        if (!box || box.hidden) return;
        const index = box.lbIndexNow();
        box.querySelectorAll('video').forEach((video) => video.pause());
        box.hidden = true;
        document.documentElement.classList.remove('pb-lightbox-open');
        if (!fromHistory && history.state?.pbLightbox) history.back();
        this.lightboxOnClose?.(index);
        this.lightboxReturnFocus?.focus({ preventScroll: true });
      }

      renderFeaturedThumbs(images) {
        const max = 4;
        const extra = images.length - max;
        return images
          .slice(0, max)
          .map((src, index) => {
            const isMore = extra > 0 && index === max - 1;
            const target = isMore ? max : index;
            return `<button type="button" class="pb-feature__thumb${index === this.galleryIndex ? ' is-active' : ''}${isMore ? ' is-more' : ''}" data-picker-thumb="${target}" aria-label="${isMore ? `${extra} more photos` : `View photo ${index + 1}`}" aria-pressed="${index === this.galleryIndex ? 'true' : 'false'}">
              <img src="${this.escapeAttribute(src)}" alt="" width="88" height="88" loading="lazy">
              ${isMore ? `<span class="pb-feature__more">+${extra}</span>` : ''}
            </button>`;
          })
          .join('');
      }

      getPairPrice(kit) {
        const kitDeal = this.getBundleDealPrice(kit.price, kit.compareAt || 0);
        const kitCompare = this.effectiveCompareAt(kit.price, kit.compareAt || 0);
        if (!this.hasAttribute('data-pair-price-live')) {
          return { sale: kitDeal * 2, compare: kitCompare * 2 };
        }

        const second = this.getSelectedAddon();
        if (second) {
          return {
            sale: kitDeal + this.getBundleDealPrice(second.price || 0, second.compareAt || 0),
            compare: kitCompare + this.effectiveCompareAt(second.price || 0, second.compareAt || 0),
          };
        }

        const cheapest = this.getPickerCatalog().reduce((lowest, item) => {
          const deal = this.getBundleDealPrice(item.price || 0, item.compareAt || 0);
          if (lowest && lowest.deal <= deal) return lowest;
          return { deal, compare: this.effectiveCompareAt(item.price || 0, item.compareAt || 0) };
        }, null);
        if (!cheapest) return { sale: kitDeal * 2, compare: kitCompare * 2 };
        return { sale: kitDeal + cheapest.deal, compare: kitCompare + cheapest.compare };
      }

      getFeaturedAtcPrice(kit) {
        const second = this.getSelectedAddon();
        if (!second) return kit.price;
        return (
          this.getBundleDealPrice(kit.price, kit.compareAt || 0) +
          this.getBundleDealPrice(second.price || 0, second.compareAt || 0)
        );
      }

      renderSecondKitMenu(featured) {
        const options = this.getPickerCatalog();
        if (!options.length) return '';
        const save = Math.round(this.discountPercent) || 30;
        return `<div class="pb-upsell" data-picker-upsell>
          <p class="pb-upsell__heading">Select a second kit for ${save}% OFF</p>
          <div class="pb-upsell__panel">
            ${options
              .map((item) => {
                const selected = String(this.selectedAddonId) === String(item.catalogId);
                const image = item.image || (item.images && item.images[0]) || '';
                const deal = this.getBundleDealPrice(item.price || 0, item.compareAt || 0);
                const compare = this.effectiveCompareAt(item.price || 0, item.compareAt || 0);
                return `<button type="button" class="pb-upsell__card${selected ? ' is-selected' : ''}" data-picker-second="${this.escapeAttribute(String(item.catalogId))}" aria-pressed="${selected ? 'true' : 'false'}">
                  ${image ? `<img src="${this.escapeAttribute(image)}" alt="" width="88" height="88" loading="lazy">` : ''}
                  <span class="pb-upsell__meta">
                    <strong>${this.escapeHtml(item.title)}</strong>
                    <span class="product-bundle__price product-bundle__price--inline">
                      <span class="product-bundle__price-sale">${this.formatMoney(deal, false)}</span>
                      ${compare > deal ? `<s class="product-bundle__price-compare">${this.formatMoney(compare, false)}</s>` : ''}
                    </span>
                  </span>
                </button>`;
              })
              .join('')}
          </div>
        </div>`;
      }

      renderAltKits(alts) {
        const wrap = this.querySelector('.pb-alts');
        const root = this.querySelector('[data-picker-alts]');
        if (wrap) wrap.hidden = alts.length === 0;
        if (!root) return;

        root.innerHTML = alts
          .map((item) => {
            const off = this.percentOff(item.price, item.compareAt || 0);
            const image = item.image || (item.images && item.images[0]) || '';
            return `<button type="button" class="pb-alts__card" data-picker-feature="${this.escapeAttribute(String(item.catalogId))}">
              ${image ? `<img src="${this.escapeAttribute(image)}" alt="" width="240" height="240" loading="lazy">` : ''}
              <span class="pb-alts__title">${this.escapeHtml(item.title)}</span>
              <span class="pb-alts__price">
                <span>${this.formatMoney(item.price, false)}</span>
                ${item.compareAt > item.price ? `<s>${this.formatMoney(item.compareAt, false)}</s>` : ''}
              </span>
              ${off > 0 ? `<span class="pb-alts__off">${off}% OFF</span>` : ''}
            </button>`;
          })
          .join('');
      }

      bindPickerShop() {
        this.querySelectorAll('[data-picker-feature]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.setFeaturedKit(button.getAttribute('data-picker-feature'), { scrollToModule: true });
          });
        });

        this.querySelectorAll('[data-picker-pack]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectPack(parseInt(button.getAttribute('data-picker-pack'), 10) || 1);
          });
        });

        this.querySelectorAll('[data-picker-second]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            this.selectSecondKit(button.getAttribute('data-picker-second'));
          });
        });

        this.querySelector('[data-picker-box]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.boxOpen = !this.boxOpen;
          this.renderPickerShop();
        });

        this.querySelector('[data-picker-build]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.buildOpen = !this.buildOpen;
          this.renderPickerShop();
          if (this.buildOpen) {
            requestAnimationFrame(() => {
              const video = this.querySelector('[data-picker-build-video]');
              if (!video) return;
              const play = video.play();
              if (play && typeof play.catch === 'function') play.catch(() => {});
            });
          }
        });

        this.querySelector('[data-picker-prev]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.shiftGallery(-1);
        });
        this.querySelector('[data-picker-next]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.shiftGallery(1);
        });
        this.querySelectorAll('[data-picker-thumb]').forEach((thumb) => {
          thumb.addEventListener('click', (event) => {
            event.preventDefault();
            this.galleryIndex = parseInt(thumb.getAttribute('data-picker-thumb'), 10) || 0;
            this.renderPickerShop();
          });
        });
        this.querySelector('[data-picker-expand]')?.addEventListener('click', (event) => {
          event.preventDefault();
          const hero = this.querySelector('[data-picker-hero]');
          if (hero?.src) window.open(hero.src, '_blank', 'noopener');
        });
        this.querySelector('[data-picker-atc]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.syncQuantityToForm();
          const submit = document.getElementById(this.dataset.submitId);
          submit?.click();
        });
      }

      formatWhole(cents) {
        return cents % 100 === 0 ? `$${cents / 100}` : this.formatMoney(cents);
      }

      formatPieces(value) {
        const digits = String(value || '').match(/\d[\d,]*/);
        return digits ? `${digits[0]} pieces` : String(value || '');
      }

      bogoMeta(kit) {
        return [this.formatPieces(kit.pieces), kit.buildTime ? this.formatBuildTime(kit.buildTime) : '']
          .filter(Boolean)
          .join(' · ');
      }

      getBogoState() {
        const kit = this.getFeaturedKit();
        const options = this.getAltKits();
        const second = options.find((item) => String(item.catalogId) === String(this.selectedAddonId)) || null;
        const price = this.bogo.price;
        const listPrice = (item) => Math.max(item?.compareAt || 0, item?.price || 0);
        if (second) {
          const value = listPrice(kit) + listPrice(second);
          return { kit, options, second, price, value, save: Math.max(0, value - price) };
        }
        const best = options
          .filter((item) => item.available !== false)
          .reduce((max, item) => Math.max(max, listPrice(item)), 0);
        const value = listPrice(kit) + best;
        return { kit, options, second: null, price, value, save: Math.max(0, value - price) };
      }

      mountBogo(root, kit) {
        const card = root.querySelector('.pb-feature__card');
        const buy = card?.querySelector('.pb-feature__buy');
        if (!card || !buy) return;

        card
          .querySelectorAll('.pb-ship, .pb-perk, .pb-packs, .pb-upsell, .pb-feature__checkout')
          .forEach((node) => node.remove());

        const config = this.bogo;
        const state = this.getBogoState();
        const perKit = this.hasAttribute('data-bogo-per-kit');
        const perKitCents = Math.round(state.price / 2);
        const benefitIcon = perKit ? this.bogoCheckFillIcon() : this.trustIcon('check');
        const benefits = (config.benefits || [])
          .filter(Boolean)
          .map((text) => `<li>${benefitIcon}<span>${this.escapeHtml(text)}</span></li>`)
          .join('');
        const offer = perKit
          ? `<div class="pb-bogo__offer">
              ${config.badge ? `<span class="pb-bogo__badge">${this.escapeHtml(config.badge)}</span>` : ''}
              <p class="pb-bogo__heading">${this.escapeHtml(config.heading || '')}</p>
              <p class="pb-bogo__hero">
                <span class="pb-bogo__now">${this.formatWhole(perKitCents)}</span>
                <span class="pb-bogo__unit">per kit</span>
              </p>
              <span class="pb-bogo__save pb-bogo__save--soft" data-bogo-save></span>
              <div class="pb-bogo__bundle">
                <p class="pb-bogo__bundle-label">Your 2-kit bundle</p>
                <p class="pb-bogo__bundle-price">
                  <span class="pb-bogo__bundle-now">${this.formatWhole(state.price)}</span>
                  <s class="pb-bogo__was" data-bogo-was></s>
                </p>
                <div class="pb-bogo__afterpay" data-picker-afterpay></div>
              </div>
              <ul class="pb-bogo__benefits">${benefits}</ul>
            </div>`
          : `<div class="pb-bogo__offer">
              ${config.badge ? `<span class="pb-bogo__badge">${this.escapeHtml(config.badge)}</span>` : ''}
              <p class="pb-bogo__heading">${this.escapeHtml(config.heading || '')}</p>
              <p class="pb-bogo__price">
                <span class="pb-bogo__now">${this.formatWhole(state.price)}</span>
                <s class="pb-bogo__was" data-bogo-was></s>
                <span class="pb-bogo__save" data-bogo-save></span>
              </p>
              <p class="pb-bogo__per-kit">
                <span class="pb-bogo__per-kit-count">${this.boxIcon()}<span>2 Builds</span></span>
                <span class="pb-bogo__per-kit-price"><strong>${this.formatWhole(perKitCents)}</strong> each</span>
              </p>
              <div class="pb-bogo__afterpay" data-picker-afterpay></div>
              <ul class="pb-bogo__benefits">${benefits}</ul>
            </div>`;
        buy.insertAdjacentHTML(
          'afterend',
          `<div class="pb-bogo${perKit ? ' pb-bogo--per-kit' : ''}" data-bogo-root>
            ${offer}

            ${this.bogoSelect(
              '1',
              config.build1Label || 'Build 1',
              this.getPickerCatalog(),
              this.bogoFirstSelected ? String(kit.catalogId) : '',
              'Select your first build'
            )}
            ${this.bogoSelect(
              '2',
              config.build2Label || 'Choose your second build',
              state.options,
              state.second ? String(state.second.catalogId) : '',
              'Select your second build'
            )}

            <div class="pb-bogo__summary" data-bogo-summary hidden>
              <p class="pb-bogo__summary-title">Your bundle</p>
              <div class="pb-bogo__pair" data-bogo-pair></div>
              <p class="pb-bogo__total" data-bogo-total></p>
            </div>

            <button type="button" class="pb-bogo__cta" data-bogo-atc disabled>
              <span class="pb-bogo__cta-spinner" aria-hidden="true"></span>
              <span data-bogo-atc-label></span>
            </button>
            ${config.guarantee ? `<p class="pb-bogo__guarantee">${this.trustIcon('shield')}<span>${this.escapeHtml(config.guarantee)}</span></p>` : ''}
            <p class="pb-bogo__error" data-bogo-error role="alert" hidden></p>
          </div>
          <div class="pb-bogo pb-bogo--single" data-bogo-single-root>
            ${this.bogoSelect('s', 'Prefer a single kit?', this.getPickerCatalog(), String(kit.catalogId), '')}
            <button type="button" class="pb-bogo__cta pb-bogo__cta--single" data-bogo-single-atc${kit.available === false ? ' disabled' : ''}>
              <span class="pb-bogo__cta-spinner" aria-hidden="true"></span>
              <span data-bogo-single-label>${this.bogoSingleLabel(kit)}</span>
            </button>
            <p class="pb-bogo__error" data-bogo-single-error role="alert" hidden></p>
          </div>`
        );
        buy.remove();

        const bogoRoot = card.querySelector('[data-bogo-root]');
        const singleRoot = card.querySelector('[data-bogo-single-root]');
        root.querySelector('.pb-feature__trust')?.remove();
        const box = root.querySelector('.pb-feature__details > .pb-feature__box:not(.pb-feature__box--build)');
        if (box) {
          box.querySelector('[data-picker-box] > svg')?.remove();
          box.querySelector('[data-picker-box] .pb-feature__box-sub')?.remove();
          const boxLabel = box.querySelector('[data-picker-box] .pb-feature__box-label');
          if (boxLabel) boxLabel.textContent = "What's in the box?";
          bogoRoot.querySelector('.pb-bogo__offer')?.after(box);
        }
        this.bindBogoSelects(bogoRoot);
        this.bindBogoSelects(singleRoot);
        if (this.singleKeepOpen) singleRoot.querySelector('[data-bogo-trigger]')?.click();
        bogoRoot.querySelector('[data-bogo-atc]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.submitBogo();
        });
        singleRoot.querySelector('[data-bogo-single-atc]')?.addEventListener('click', (event) => {
          event.preventDefault();
          this.submitBogo({ single: true });
        });
        this.updateBogo();
      }

      bogoSingleLabel(kit) {
        return kit?.available === false ? 'Sold out' : `Add Single Kit to Cart — ${this.formatWhole(kit?.price || 0)}`;
      }

      bogoThumb(item) {
        return item.image
          ? `<img class="pb-bogo__thumb" src="${this.escapeAttribute(item.image)}" alt="" width="64" height="64" loading="lazy">`
          : '<span class="pb-bogo__thumb" aria-hidden="true"></span>';
      }

      bogoSaveLine(item) {
        if (!item || item.available === false) return '';
        const price = item.price || 0;
        const compareAt = item.compareAt || 0;
        if (!(compareAt > price)) return '';
        const save = compareAt - price;
        const percent = this.percentOff(price, compareAt);
        const saveLabel = this.bogo?.saveLabel || 'Save';
        return `<span class="pb-bogo__deal">
            <span class="pb-bogo__deal-now">${this.formatWhole(price)}</span>
            <s class="pb-bogo__deal-was">${this.formatWhole(compareAt)}</s>
            <span class="pb-bogo__deal-save">${saveLabel} ${this.formatWhole(save)} · ${percent}% OFF</span>
          </span>`;
      }

      bogoValueHtml(item, placeholder, { showSave = false } = {}) {
        if (!item) {
          return `<span class="pb-bogo__thumb pb-bogo__thumb--empty" aria-hidden="true">+</span>
            <span class="pb-bogo__copy">
              <strong class="pb-bogo__name">${this.escapeHtml(placeholder)}</strong>
              <span class="pb-bogo__meta">Tap to see all builds</span>
            </span>`;
        }
        const soldOut = item.available === false;
        return `${this.bogoThumb(item)}
          <span class="pb-bogo__copy">
            <strong class="pb-bogo__name">${this.escapeHtml(item.title)}</strong>
            <span class="pb-bogo__meta">${soldOut ? 'Sold out' : this.escapeHtml(this.bogoMeta(item))}</span>
            ${showSave ? this.bogoSaveLine(item) : ''}
          </span>`;
      }

      bogoSelect(step, label, options, selectedId, placeholder) {
        const base = `pb-bogo-${step}-${this.sectionId}`;
        const selected = options.find((item) => String(item.catalogId) === selectedId) || null;
        const numbered = step !== 's';
        const showOptionSave = step === 's';
        return `<p class="pb-bogo__label${step === '1' ? ' pb-bogo__label--first' : ''}${numbered ? '' : ' pb-bogo__label--single'}" id="${base}-label">${numbered ? `<span class="pb-bogo__step">${step}.</span> ` : ''}${this.escapeHtml(label)}</p>
          <div class="pb-bogo__select" data-bogo-select="${step}" data-placeholder="${this.escapeAttribute(placeholder)}">
            <button type="button" class="pb-bogo__row pb-bogo__trigger${selected ? ' is-filled' : ''}" data-bogo-trigger
              aria-haspopup="listbox" aria-expanded="false" aria-controls="${base}-list" aria-labelledby="${base}-label ${base}-value">
              <span class="pb-bogo__value" id="${base}-value" data-bogo-value>${this.bogoValueHtml(selected, placeholder)}</span>
              <span class="pb-bogo__chevron" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
            </button>
            <ul class="pb-bogo__options" id="${base}-list" role="listbox" aria-labelledby="${base}-label" data-bogo-list hidden>
              ${options
                .map((item) => {
                  const id = String(item.catalogId);
                  const soldOut = item.available === false;
                  const on = id === selectedId;
                  return `<li class="pb-bogo__row pb-bogo__option${on ? ' is-selected' : ''}${soldOut ? ' is-disabled' : ''}" role="option" tabindex="-1"
                    data-bogo-option="${this.escapeAttribute(id)}" aria-selected="${on}"${soldOut ? ' aria-disabled="true"' : ''}>
                    ${this.bogoValueHtml(item, '', { showSave: showOptionSave })}
                    <span class="pb-bogo__radio" aria-hidden="true"></span>
                  </li>`;
                })
                .join('')}
            </ul>
          </div>`;
      }

      bindBogoSelects(bogoRoot) {
        const selects = [...bogoRoot.querySelectorAll('[data-bogo-select]')];
        const toggle = (select, open, focus) => {
          const trigger = select.querySelector('[data-bogo-trigger]');
          const list = select.querySelector('[data-bogo-list]');
          select.classList.toggle('is-open', open);
          trigger.setAttribute('aria-expanded', String(open));
          list.hidden = !open;
          if (open) {
            selects.filter((other) => other !== select).forEach((other) => toggle(other, false));
            if (focus) (list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]'))?.focus();
          } else if (focus) {
            trigger.focus();
          }
        };
        const choose = (select, option) => {
          if (option.getAttribute('aria-disabled') === 'true') return;
          const id = option.getAttribute('data-bogo-option');
          const step = select.getAttribute('data-bogo-select');
          if (step === 's') {
            if (String(this.selectedAddonId) === id) this.selectedAddonId = String(this.getFeaturedKit().catalogId);
            this.singleKeepOpen = true;
            this.setFeaturedKit(id);
            this.singleKeepOpen = false;
            this.querySelector('[data-bogo-select="s"] [aria-selected="true"]')?.focus();
            return;
          }
          toggle(select, false, true);
          if (step !== '2') {
            if (String(this.selectedAddonId) === id) this.selectedAddonId = String(this.getFeaturedKit().catalogId);
            this.bogoFirstSelected = true;
            const sameFeatured = String(id) === String(this.featuredId);
            this.setFeaturedKit(id);
            if (sameFeatured) this.updateBogo();
            this.querySelector(`[data-bogo-select="${step}"] [data-bogo-trigger]`)?.focus();
          } else {
            if (!this.bogoFirstSelected) return;
            this.selectSecondKit(id);
          }
        };

        selects.forEach((select) => {
          const trigger = select.querySelector('[data-bogo-trigger]');
          const list = select.querySelector('[data-bogo-list]');
          trigger.addEventListener('click', () => toggle(select, list.hidden));
          trigger.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              toggle(select, true, true);
            }
          });
          list.addEventListener('click', (event) => {
            const option = event.target.closest('[role="option"]');
            if (option) choose(select, option);
          });
          list.addEventListener('keydown', (event) => {
            const options = [...list.querySelectorAll('[role="option"]')];
            const index = options.indexOf(document.activeElement);
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              event.preventDefault();
              const next = index + (event.key === 'ArrowDown' ? 1 : -1);
              options[(next + options.length) % options.length]?.focus();
            } else if (event.key === 'Home' || event.key === 'End') {
              event.preventDefault();
              options[event.key === 'Home' ? 0 : options.length - 1]?.focus();
            } else if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              if (options[index]) choose(select, options[index]);
            } else if (event.key === 'Escape' || event.key === 'Tab') {
              if (event.key === 'Escape') event.preventDefault();
              toggle(select, false, event.key === 'Escape');
            }
          });
        });
      }

      updateBogo() {
        const bogoRoot = this.querySelector('[data-bogo-root]');
        if (!bogoRoot) return;
        const state = this.getBogoState();
        const config = this.bogo;
        const firstKit = this.bogoFirstSelected ? state.kit : null;
        const firstId = firstKit ? String(firstKit.catalogId) : '';
        const selectedId = state.second ? String(state.second.catalogId) : '';
        const bundleReady = Boolean(firstKit && state.second);

        const first = bogoRoot.querySelector('[data-bogo-select="1"]');
        if (first) {
          first.querySelectorAll('[data-bogo-option]').forEach((row) => {
            const on = row.getAttribute('data-bogo-option') === firstId;
            row.classList.toggle('is-selected', on);
            row.setAttribute('aria-selected', String(on));
          });
          first.querySelector('[data-bogo-trigger]')?.classList.toggle('is-filled', Boolean(firstKit));
          const value = first.querySelector('[data-bogo-value]');
          if (value) value.innerHTML = this.bogoValueHtml(firstKit, first.dataset.placeholder || '');
        }

        const second = bogoRoot.querySelector('[data-bogo-select="2"]');
        if (second) {
          second.classList.toggle('is-locked', !this.bogoFirstSelected);
          const trigger = second.querySelector('[data-bogo-trigger]');
          if (trigger) {
            trigger.disabled = !this.bogoFirstSelected;
            trigger.classList.toggle('is-filled', Boolean(state.second));
          }
          second.querySelectorAll('[data-bogo-option]').forEach((row) => {
            const on = row.getAttribute('data-bogo-option') === selectedId;
            row.classList.toggle('is-selected', on);
            row.setAttribute('aria-selected', String(on));
          });
          const value = second.querySelector('[data-bogo-value]');
          if (value) value.innerHTML = this.bogoValueHtml(state.second, second.dataset.placeholder || '');
        }

        const was = bogoRoot.querySelector('[data-bogo-was]');
        const save = bogoRoot.querySelector('[data-bogo-save]');
        if (was) was.textContent = state.save > 0 ? this.formatWhole(state.value) : '';
        if (save) {
          save.hidden = state.save <= 0;
          const percent = state.value > 0 ? Math.round((state.save / state.value) * 100) : 0;
          save.textContent = `${config.saveLabel || 'Save'}${bundleReady ? '' : ' up to'} ${percent}%`;
        }

        const summary = bogoRoot.querySelector('[data-bogo-summary]');
        if (summary) {
          summary.hidden = !bundleReady;
          if (bundleReady) {
            const piece = (item) =>
              `<span class="pb-bogo__pair-item">${item.image ? `<img src="${this.escapeAttribute(item.image)}" alt="" width="44" height="44" loading="lazy">` : ''}<span>${this.escapeHtml(item.title)}</span></span>`;
            summary.querySelector('[data-bogo-pair]').innerHTML =
              `${piece(state.kit)}<span class="pb-bogo__plus" aria-hidden="true">+</span>${piece(state.second)}`;
            const total = summary.querySelector('[data-bogo-total]');
            if (total) {
              const saveLabel = config.saveLabel || 'Save';
              if (state.save > 0 && state.value > 0) {
                const percent = Math.round((state.save / state.value) * 100);
                total.innerHTML = `<span class="pb-bogo__deal">
                    <span class="pb-bogo__deal-now">${this.formatWhole(state.price)}</span>
                    <s class="pb-bogo__deal-was">${this.formatWhole(state.value)}</s>
                    <span class="pb-bogo__deal-save">${saveLabel} ${this.formatWhole(state.save)} · ${percent}% OFF</span>
                  </span>`;
              } else {
                total.innerHTML = `<span class="pb-bogo__deal"><span class="pb-bogo__deal-now">${this.formatWhole(state.price)}</span></span>`;
              }
            }
          }
        }

        const atc = bogoRoot.querySelector('[data-bogo-atc]');
        const label = bogoRoot.querySelector('[data-bogo-atc-label]');
        if (atc && !this.bogoBusy) {
          atc.disabled = !bundleReady;
          if (label) {
            label.textContent = bundleReady
              ? `Add 2 Builds to Cart — ${this.formatWhole(state.price)}`
              : this.bogoFirstSelected
                ? 'Choose Your Second Build'
                : 'Choose Your First Build';
          }
        }
      }

      setBogoBusy(busy) {
        this.bogoBusy = busy;
        const atc = this.querySelector('[data-bogo-atc]');
        const single = this.querySelector('[data-bogo-single-atc]');
        const target = this.bogoSingle ? single : atc;
        const label = this.querySelector(this.bogoSingle ? '[data-bogo-single-label]' : '[data-bogo-atc-label]');
        if (target) {
          target.classList.toggle('is-loading', busy);
          target.setAttribute('aria-busy', busy ? 'true' : 'false');
        }
        if (atc) atc.disabled = busy;
        if (single) single.disabled = busy;
        if (busy && label) label.textContent = 'Adding to cart…';
        if (!busy) {
          clearTimeout(this.bogoTimer);
          this.bogoUnsubs?.forEach((unsubscribe) => unsubscribe?.());
          this.bogoUnsubs = null;
          if (this.bogoSingle) {
            this.bogoSingle = false;
            this.setPurchaseMode('bundle');
          }
          const kit = this.getFeaturedKit();
          const singleLabel = this.querySelector('[data-bogo-single-label]');
          if (singleLabel) singleLabel.textContent = this.bogoSingleLabel(kit);
          if (single) single.disabled = kit?.available === false;
          this.updateBogo();
        }
      }

      setPurchaseMode(mode) {
        const input = this.querySelector(`[data-bundle-input][value="${mode}"]`);
        if (!input) return;
        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      submitBogo({ single = false } = {}) {
        if (this.bogoBusy) return;
        const state = this.getBogoState();
        if (single ? state.kit?.available === false : !(this.bogoFirstSelected && state.second)) return;

        this.querySelectorAll('[data-bogo-error], [data-bogo-single-error]').forEach((node) => (node.hidden = true));
        const error = this.querySelector(single ? '[data-bogo-single-error]' : '[data-bogo-error]');

        const submit = document.getElementById(this.dataset.submitId);
        if (!submit) return;

        this.bogoSingle = single;
        if (single) {
          // Single kit must not go through the bundle add path (which needs a 2nd build).
          this.selectedAddonId = null;
          this.setPurchaseMode('kit');
        } else {
          this.setPurchaseMode('bundle');
        }
        this.qty = 1;
        this.syncQuantityToForm();

        // Ensure the product-form submit button can fire after a prior failed attempt.
        submit.removeAttribute('aria-disabled');
        submit.classList.remove('loading');

        this.setBogoBusy(true);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.bogoUnsubs = [
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.setBogoBusy(false)),
            subscribe(PUB_SUB_EVENTS.cartError, (event) => {
              if (error) {
                error.textContent = event?.message || event?.errors || 'Something went wrong. Please try again.';
                error.hidden = false;
              }
              this.setBogoBusy(false);
            }),
          ];
        }
        this.bogoTimer = setTimeout(() => this.setBogoBusy(false), 15000);

        submit.click();
        requestAnimationFrame(() => {
          if (!submit.classList.contains('loading') && this.bogoBusy) this.setBogoBusy(false);
        });
      }

      updatePickerSelection() {
        if (this.bogo && this.querySelector('[data-bogo-root]')) {
          this.updateBogo();
          return true;
        }
        if (!this.isPicker() || !this.hasAttribute('data-picker-inplace')) return false;
        const kit = this.getFeaturedKit();
        const card = this.querySelector('[data-picker-featured] .pb-feature__card');
        if (!kit || !card) return false;

        const packTwo = this.packSize === 2;
        card.querySelectorAll('[data-picker-pack]').forEach((button) => {
          const on = (button.getAttribute('data-picker-pack') === '2') === packTwo;
          button.classList.toggle('is-selected', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        const { sale, compare } = this.getPairPrice(kit);
        const pairPrice = card.querySelector('[data-picker-pack="2"] .pb-pack__price');
        if (pairPrice) {
          pairPrice.innerHTML = `<span>${this.formatMoney(sale, false)}</span>${
            compare > sale ? `<s>${this.formatMoney(compare, false)}</s>` : ''
          }`;
        }

        let menu = card.querySelector('[data-picker-upsell]');
        if (packTwo && !menu) {
          card.querySelector('.pb-packs')?.insertAdjacentHTML('afterend', this.renderSecondKitMenu(kit));
          menu = card.querySelector('[data-picker-upsell]');
          menu?.querySelectorAll('[data-picker-second]').forEach((button) => {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              this.selectSecondKit(button.getAttribute('data-picker-second'));
            });
          });
        } else if (!packTwo && menu) {
          menu.remove();
          menu = null;
        }
        menu?.querySelectorAll('[data-picker-second]').forEach((button) => {
          const on = String(this.selectedAddonId) === button.getAttribute('data-picker-second');
          button.classList.toggle('is-selected', on);
          button.setAttribute('aria-pressed', on ? 'true' : 'false');
        });

        const atcLabel = card.querySelector('[data-picker-atc] span');
        if (atcLabel) {
          atcLabel.innerHTML = `Add to bag — ${this.formatMoney(this.getFeaturedAtcPrice(kit) * (this.qty || 1), false)}`;
        }
        return true;
      }

      selectPack(size) {
        const next = size === 2 ? 2 : 1;
        if (this.packSize === next) return;
        this.packSize = next;
        if (next === 2) {
          this.selectedAddonId = null;
          const bundleInput = this.querySelector('[data-bundle-input][value="bundle"]');
          if (bundleInput && !bundleInput.checked) {
            bundleInput.checked = true;
            bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          this.selectedAddonId = null;
          const kitInput = this.querySelector('[data-bundle-input][value="kit"]');
          if (kitInput && !kitInput.checked) {
            kitInput.checked = true;
            kitInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        if (!this.updatePickerSelection()) this.renderChooser();
        this.renderIncludedProducts();
        this.syncBundlePanel();
        this.updatePrices();
      }

      syncQuantityToForm() {
        if (!this.form) return;
        let input = this.form.querySelector('input[name="quantity"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'quantity';
          this.form.appendChild(input);
        }
        input.value = String(this.qty || 1);
      }

      shiftGallery(delta) {
        const kit = this.getFeaturedKit();
        const images = (kit.images && kit.images.length ? kit.images : [kit.image]).filter(Boolean);
        if (images.length < 2) return;
        this.galleryIndex = (this.galleryIndex + delta + images.length) % images.length;
        this.renderPickerShop();
      }

      setFeaturedKit(id, options = {}) {
        if (!id || String(id) === String(this.featuredId)) return;

        this.featuredId = id;
        this.galleryIndex = 0;
        this.boxOpen = false;
        this.buildOpen = false;

        const featured = this.getFeaturedKit();
        const variantInput = this.form?.querySelector('[name="id"]');
        if (variantInput && featured?.variantId) {
          variantInput.value = featured.variantId;
          variantInput.disabled = false;
        }
        this.currentKitPrice = featured.price || 0;
        this.currentKitCompareAt = featured.compareAt || 0;

        if (this.packSize !== 2 && String(this.selectedAddonId) === String(id)) {
          this.selectedAddonId = null;
        }

        const wantsBundle = this.packSize === 2 || Boolean(this.selectedAddonId);
        const kitInput = this.querySelector('[data-bundle-input][value="kit"]');
        const bundleInput = this.querySelector('[data-bundle-input][value="bundle"]');
        if (wantsBundle && bundleInput) {
          bundleInput.checked = true;
          bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (kitInput) {
          kitInput.checked = true;
          kitInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        this.renderChooser();
        this.renderIncludedProducts();
        this.syncBundlePanel();
        this.updatePrices();
        if (options.scrollToModule && this.isPicker()) this.transitionToPurchaseModule();
      }

      transitionToPurchaseModule() {
        const featured = this.querySelector('[data-picker-featured]');
        const target = document.getElementById('purchase-module') || featured;
        if (!target) return;

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (featured && !reduce) {
          featured.classList.remove('is-enter');
          void featured.offsetWidth;
          featured.classList.add('is-enter');
        }

        const header = document.querySelector('sticky-header');
        const offset = (header ? header.getBoundingClientRect().height : 0) + 12;
        const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - offset);
        this.animateScrollTo(top, reduce ? 0 : 720);
      }

      animateScrollTo(top, duration) {
        const start = window.scrollY;
        const delta = top - start;
        if (Math.abs(delta) < 2) return;
        if (!duration) {
          window.scrollTo(0, top);
          return;
        }

        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        const started = performance.now();
        const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
        const step = (now) => {
          const t = Math.min(1, (now - started) / duration);
          window.scrollTo(0, start + delta * ease(t));
          if (t < 1) this.scrollFrame = requestAnimationFrame(step);
        };
        this.scrollFrame = requestAnimationFrame(step);
      }

      selectSecondKit(id) {
        if (!id) return;
        this.packSize = 2;
        this.selectedAddonId = String(id);
        const bundleInput = this.querySelector('[data-bundle-input][value="bundle"]');
        if (bundleInput && !bundleInput.checked) {
          bundleInput.checked = true;
          bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (!this.updatePickerSelection()) this.renderChooser();
        this.renderIncludedProducts();
        this.syncBundlePanel();
        this.updatePrices();
      }

      specIcon(name) {
        if (name === 'pieces') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="4" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="4" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="4" y="14" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.6"/><rect x="14" y="14" width="6" height="6" rx="1" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
        }
        if (name === 'height') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h8M12 4v16M8 20h8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
        }
        if (name === 'time') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M12 8v4.2L15 15" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        }
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="14" width="4" height="6" rx="0.8" fill="currentColor"/><rect x="10" y="10" width="4" height="10" rx="0.8" fill="currentColor"/><rect x="16" y="6" width="4" height="14" rx="0.8" fill="currentColor" opacity="0.45"/></svg>`;
      }

      boxIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M4 8l8-4 8 4M12 8v13" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
      }

      playIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.8 6.4v11.2L18.2 12z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>`;
      }

      sparkIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2.2l1.55 8.25L22 12l-8.45 1.55L12 21.8l-1.55-8.25L2 12l8.45-1.55z" fill="currentColor"/></svg>`;
      }

      chevronIcon(dir) {
        const paths = {
          prev: 'M14.5 5.5L8 12l6.5 6.5',
          next: 'M9.5 5.5L16 12l-6.5 6.5',
          down: 'M6 9.5L12 15.5 18 9.5',
          up: 'M6 14.5L12 8.5 18 14.5',
        };
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${paths[dir] || paths.next}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      }

      expandIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>`;
      }

      giftIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12v8.2a1.4 1.4 0 0 1-1.4 1.4H5.4A1.4 1.4 0 0 1 4 20.2V12M3.4 7.4h17.2v4.6H3.4zM12 22V7.4M12 7.4H7.6a2.4 2.4 0 1 1 0-4.8C11.2 2.6 12 7.4 12 7.4zM12 7.4h4.4a2.4 2.4 0 1 0 0-4.8C12.8 2.6 12 7.4 12 7.4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      }

      bagIcon() {
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.4 8.2h11.2l.8 11.2H5.6L6.4 8.2z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M9 8.2V6.6A3 3 0 0 1 12 3.6a3 3 0 0 1 3 3v1.6" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>`;
      }

      bogoCheckFillIcon() {
        return `<svg class="pb-bogo__check-fill" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="11" fill="currentColor"/><path d="M7.2 12.2l3.1 3.1 6.5-6.8" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
      }

      trustIcon(name) {
        if (name === 'truck') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 7h11v8H3zM14 10h4l3 3v2h-7z" fill="none" stroke="currentColor" stroke-width="1.6"/><circle cx="7" cy="18" r="1.7" fill="none" stroke="currentColor" stroke-width="1.6"/><circle cx="17" cy="18" r="1.7" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
        }
        if (name === 'wrench') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14.8 6.2a3.4 3.4 0 0 1 3 3.2c0 .3 0 .6-.1.9l-6.2 6.2-2.6-.2-.2-2.6 6.2-6.2c.3-.1.6-.1.9-.1z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M8.6 16.8L5.4 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
        }
        if (name === 'check') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8.4 12.3l2.4 2.4 4.8-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        }
        if (name === 'box') {
          return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 8l8-4 8 4v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8zM4 8h16M12 8v12" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>`;
        }
        return `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 3v6c0 5-3.4 8.4-8 9.5C7.4 20.4 4 17 4 12V6l8-3z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8.5 12.2l2.2 2.2 4.8-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>`;
      }

      selectAddon(addonId) {
        if (!addonId || addonId === 'kit') {
          this.selectedAddonId = null;
          const kitInput = this.querySelector('[data-bundle-input][value="kit"]');
          if (kitInput && !kitInput.checked) {
            kitInput.checked = true;
            kitInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.renderChooser();
          this.renderIncludedProducts();
          this.syncBundlePanel();
          this.updatePrices();
          return;
        }

        this.selectedAddonId = addonId;
        const bundleInput = this.querySelector('[data-bundle-input][value="bundle"]');
        if (bundleInput && !bundleInput.checked) {
          bundleInput.checked = true;
          bundleInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.renderChooser();
        this.renderIncludedProducts();
        this.syncBundlePanel();
        this.updatePrices();
      }

      renderIncludedProducts() {
        const summary = this.querySelector('[data-bundle-summary]');
        const includedList = this.querySelector('[data-bundle-included-list]');

        if (this.getAvailableAddons().length === 0) {
          this.dataset.hasBundleAddons = 'false';
          return;
        }

        this.dataset.hasBundleAddons = 'true';

        if (summary) {
          summary.hidden = true;
        }

        if (!includedList) return;

        const selected = this.getSelectedAddon();
        const kitCard = this.renderIncludedProductCard(this.getKitProduct());
        const secondCard = selected
          ? this.renderIncludedProductCard({ ...selected, isPickable: true })
          : this.renderPlaceholderCard();

        includedList.innerHTML = `${kitCard}
          <li class="product-bundle__included-divider" aria-hidden="true"><span class="product-bundle__included-divider-icon">+</span></li>
          ${secondCard}`;

        includedList.querySelectorAll('[data-bundle-open-chooser]').forEach((button) => {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.querySelector('[data-bundle-chooser]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        });
      }

      renderPlaceholderCard() {
        const label = this.dataset.chooseLabel || 'Choose your 2nd kit';
        return `
          <li class="product-bundle__included-item product-bundle__included-item--placeholder">
            <button type="button" class="product-bundle__included-card product-bundle__included-card--placeholder" data-bundle-open-chooser>
              <span class="product-bundle__placeholder-plus" aria-hidden="true">+</span>
              <span class="product-bundle__included-title">${this.escapeHtml(label)}</span>
            </button>
          </li>
        `;
      }

      renderIncludedProductCard(item) {
        const isClickable = Boolean(item.url) && !item.isCurrent && !item.isPickable;
        const imageClasses =
          'product-bundle__included-image global-media-settings' +
          (item.isCurrent ? ' product-bundle__included-image--current' : '');

        const imageTag = item.image
          ? `<img
              class="${imageClasses}"
              src="${this.escapeAttribute(item.image)}"
              alt="${this.escapeAttribute(item.imageAlt || item.title)}"
              width="72"
              height="72"
              loading="lazy"
            >`
          : '';

        const thisKitLabel = this.dataset.thisKitLabel || 'This kit';
        const imageMarkup = imageTag
          ? isClickable
            ? `<a href="${this.escapeAttribute(item.url)}" class="product-bundle__included-link" aria-label="View ${this.escapeAttribute(item.title)}">${imageTag}</a>`
            : `<div class="product-bundle__included-media">${imageTag}</div>`
          : '';
        const eyebrow = item.isCurrent
          ? `<span class="product-bundle__included-eyebrow">${this.escapeHtml(thisKitLabel)}</span>`
          : '';

        return `
          <li class="product-bundle__included-item${item.isCurrent ? ' product-bundle__included-item--current' : ''}${item.isPickable ? ' product-bundle__included-item--pickable' : ''}">
            <div class="product-bundle__included-card">
              ${eyebrow}
              ${imageMarkup}
              <span class="product-bundle__included-title">${this.escapeHtml(item.title)}</span>
              <span class="product-bundle__included-price" data-included-price="${this.escapeAttribute(String(item.id))}">
                ${this.renderBundleDealPriceMarkup(item.price, item.compareAt || 0)}
              </span>
            </div>
          </li>
        `;
      }

      getDiscountedPrice(price) {
        if (this.discountPercent <= 0) return price;

        const discount = Math.round((price * this.discountPercent) / 100);
        const discounted = price - discount;

        return Math.round(discounted / 100) * 100;
      }

      shouldRoundPrices() {
        return this.discountPercent > 0;
      }

      renderPriceMarkup(price, layout = 'stacked') {
        const salePrice = this.getDiscountedPrice(price);
        const roundPrices = this.shouldRoundPrices();
        const hasDiscount = roundPrices && salePrice < price;

        if (hasDiscount) {
          if (layout === 'inline') {
            return `<span class="product-bundle__price product-bundle__price--inline">
              <span class="product-bundle__price-sale">${this.formatMoney(salePrice, true)}</span>
              <s class="product-bundle__price-compare">${this.formatMoney(price, false)}</s>
            </span>`;
          }

          return `<span class="product-bundle__price">
            <span class="product-bundle__price-sale">${this.formatMoney(salePrice, true)}</span>
            <s class="product-bundle__price-compare">${this.formatMoney(price, false)}</s>
          </span>`;
        }

        return `<span class="product-bundle__price${layout === 'inline' ? ' product-bundle__price--inline' : ''}">
          <span class="product-bundle__price-sale">${this.formatMoney(price, false)}</span>
        </span>`;
      }

      renderKitPriceMarkup(price, compareAt) {
        if (compareAt > price) {
          return `<span class="product-bundle__price product-bundle__price--inline">
            <span class="product-bundle__price-sale">${this.formatMoney(price, false)}</span>
            <s class="product-bundle__price-compare">${this.formatMoney(compareAt, false)}</s>
          </span>`;
        }

        return `<span class="product-bundle__price">
          <span class="product-bundle__price-sale">${this.formatMoney(price, false)}</span>
        </span>`;
      }

      /** Per-kit price inside the bundle: 20% off compare-at (matches Function / bundle total). */
      renderBundleDealPriceMarkup(price, compareAt) {
        const compareBase = this.effectiveCompareAt(price, compareAt);
        const dealPrice = this.getDiscountedPrice(compareBase);

        if (this.shouldRoundPrices() && dealPrice < compareBase) {
          return `<span class="product-bundle__price product-bundle__price--inline">
            <span class="product-bundle__price-sale">${this.formatMoney(dealPrice, true)}</span>
            <s class="product-bundle__price-compare">${this.formatMoney(compareBase, false)}</s>
          </span>`;
        }

        return this.renderKitPriceMarkup(price, compareAt);
      }

      escapeHtml(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;');
      }

      escapeAttribute(value) {
        return this.escapeHtml(value).replaceAll("'", '&#39;');
      }

      onSelectionChange(event) {
        if (this.propertyInput) {
          this.propertyInput.value =
            event.target.value === 'bundle'
              ? this.bundleLabel || 'Bundle'
              : this.kitLabel || 'Kit only';
        }
        this.syncBundlePanel();
        this.updatePrices();
      }

      updatePrices() {
        if (this.kitPriceEl) {
          this.kitPriceEl.innerHTML = this.renderKitPriceMarkup(
            this.currentKitPrice,
            this.currentKitCompareAt
          );
        }

        this.updatePickerSlots();

        if (!this.bundlePriceEl) return;

        const kitPrice = this.currentKitPrice;
        const kitCompare = this.effectiveCompareAt(kitPrice, this.currentKitCompareAt);
        const selected = this.getSelectedAddon();
        const fromLabel = this.dataset.fromLabel || 'From';

        if (!selected) {
          const cheapest = this.getAvailableAddons().reduce((lowest, addon) => {
            const compare = this.effectiveCompareAt(addon.price || 0, addon.compareAt || 0);
            if (!lowest || compare < lowest.compare) {
              return { addon, compare };
            }
            return lowest;
          }, null);

          if (!cheapest) {
            this.bundlePriceEl.textContent = '';
            return;
          }

          const fromCompare = kitCompare + cheapest.compare;
          const fromPrice =
            this.getBundleDealPrice(kitPrice, this.currentKitCompareAt) +
            this.getBundleDealPrice(cheapest.addon.price || 0, cheapest.addon.compareAt || 0);
          const fromCompareMarkup =
            fromPrice < fromCompare
              ? `<s class="product-bundle__price-compare">${this.formatMoney(fromCompare, false)}</s>`
              : '';
          this.bundlePriceEl.innerHTML = `<span class="product-bundle__price product-bundle__price--inline"><span class="product-bundle__price-from">${this.escapeHtml(fromLabel)}</span><span class="product-bundle__price-sale">${this.formatMoney(fromPrice, true)}</span>${fromCompareMarkup}</span>`;
          return;
        }

        const addonsSaleTotal = selected.price || 0;
        const addonsCompareTotal = this.effectiveCompareAt(selected.price || 0, selected.compareAt || 0);
        const saleSubtotal = kitPrice + addonsSaleTotal;
        const compareSubtotal = kitCompare + addonsCompareTotal;
        const bundlePrice =
          this.getBundleDealPrice(kitPrice, this.currentKitCompareAt) +
          this.getBundleDealPrice(selected.price || 0, selected.compareAt || 0);

        if (this.shouldRoundPrices() && bundlePrice < compareSubtotal) {
          this.bundlePriceEl.innerHTML = `<span class="product-bundle__price product-bundle__price--inline">
            <span class="product-bundle__price-sale">${this.formatMoney(bundlePrice, true)}</span>
            <s class="product-bundle__price-compare">${this.formatMoney(compareSubtotal, false)}</s>
          </span>`;
        } else {
          this.bundlePriceEl.innerHTML = `<span class="product-bundle__price">
            <span class="product-bundle__price-sale">${this.formatMoney(saleSubtotal, false)}</span>
          </span>`;
        }

        this.querySelectorAll('[data-included-price]').forEach((priceEl) => {
          const itemId = priceEl.dataset.includedPrice;
          let price = 0;
          let compareAt = 0;

          if (itemId === 'kit') {
            price = kitPrice;
            compareAt = this.currentKitCompareAt;
          } else {
            const addon = this.addons.find((entry) => String(entry.id) === String(itemId));
            price = addon?.price || 0;
            compareAt = addon?.compareAt || 0;
          }

          priceEl.innerHTML = this.renderBundleDealPriceMarkup(price, compareAt);
        });

        this.updatePickerSlots();
      }

      updatePickerSlots() {
        if (!this.isPicker()) return;

        const kitSlot = this.querySelector('[data-picker-slot="kit"] [data-picker-slot-value]');
        const secondSlot = this.querySelector('[data-picker-slot="second"]');
        const secondValue = secondSlot?.querySelector('[data-picker-slot-value]');
        if (!kitSlot || !secondSlot || !secondValue) return;

        const selected = this.getSelectedAddon();
        kitSlot.innerHTML = selected
          ? this.renderBundleDealPriceMarkup(this.currentKitPrice, this.currentKitCompareAt)
          : this.renderKitPriceMarkup(this.currentKitPrice, this.currentKitCompareAt);

        if (selected) {
          secondSlot.classList.remove('is-pending');
          secondValue.innerHTML = `<span class="pb-picker__slot-name">${this.escapeHtml(selected.title)}</span>${this.renderBundleDealPriceMarkup(selected.price, selected.compareAt || 0)}`;
        } else {
          secondSlot.classList.add('is-pending');
          secondValue.textContent = this.dataset.slotEmpty || 'Choose below';
        }
      }

      formatMoney(cents, roundToDollar = false) {
        const amount = roundToDollar ? Math.round(cents / 100) * 100 : cents;

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
          return Shopify.formatMoney(amount, this.moneyFormat);
        }

        return `$${(amount / 100).toFixed(2)}`;
      }
    }
  );
}
